'use client';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { RESPONSIVE_MARGIN } from '@/functions/createTheme';
import { MarkdownText } from '../MarkdownText';
import { Description } from './Description';
import { DetailButton } from './DetailButton';
import { EventImage } from './EventImage';
import { isFinished } from './functions';
import { GeoMap } from './GeoMap';
import { GeoMapLinkButton } from './GeoMapLinkButton';
import { InfoTable } from './InfoTable';
import { Reference } from './Reference';
import { RelatedCategories } from './RelatedCategories';
import { SupplementaryInfo } from './SupplementaryInfo';

type Props = {
  imEvent: ImEventDto;
  customerSetting: CustomerSetting;
};

export const EventInfo: React.FC<Props> = ({ imEvent, customerSetting }) => {
  const theme = useTheme();

  const finished = isFinished(imEvent);

  return (
    <Box sx={{ my: 4 }}>
      {/* イベント名 */}
      <Box sx={{ my: RESPONSIVE_MARGIN }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            fontSize: {
              xs: theme.typography.h6.fontSize,
              sm: theme.typography.h5.fontSize
            }
          }}
        >
          {imEvent.title}
        </Typography>
      </Box>
      {/* 中止イベントの場合は詳細は表示しない */}
      {imEvent.canceled && (
        <Typography color="error" variant="h6">
          当イベントは中止になりました
        </Typography>
      )}
      {/* イベントが終わっている場合は詳細は表示しない */}
      {finished && (
        <Typography color="error" variant="h6">
          当イベントは終了しました
        </Typography>
      )}
      {!imEvent.canceled && !finished && (
        <>
          {/* イベント画像 */}
          <Box sx={{ my: RESPONSIVE_MARGIN }}>
            <EventImage
              imageUrl={imEvent.imageUrl || undefined}
              imageAlt={imEvent.title}
              disappearWhenInvalidImage
            />
          </Box>
          {/* イベント詳細文 */}
          {customerSetting.info.showDescription && imEvent.description && (
            <Box sx={{ my: RESPONSIVE_MARGIN }}>
              <Description htmlText={imEvent.description} />
            </Box>
          )}
          {/* イベント詳細のテーブル */}
          <Box sx={{ my: RESPONSIVE_MARGIN }}>
            <InfoTable imEvent={imEvent} customerSetting={customerSetting} />
          </Box>
          {/* 補足情報 */}
          <Box sx={{ my: RESPONSIVE_MARGIN }}>
            <SupplementaryInfo
              imEvent={imEvent}
              customerSetting={customerSetting}
            />
          </Box>
          {/* 関連カテゴリのタグ */}
          {customerSetting.info.showCategoryTag && (
            <Box sx={{ my: RESPONSIVE_MARGIN }}>
              <RelatedCategories
                categories={imEvent.categories}
                customerSetting={customerSetting}
              />
            </Box>
          )}
          {/* 詳細を見るボタン */}
          {imEvent.url && (
            <Box
              sx={{
                my: RESPONSIVE_MARGIN,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <DetailButton url={imEvent.url} />
            </Box>
          )}
          {/* Google Map */}
          {customerSetting.info.showMap &&
            imEvent.participationType !== 'online' &&
            imEvent.latitude !== null &&
            imEvent.longitude !== null && (
              <Box sx={{ my: RESPONSIVE_MARGIN }}>
                <GeoMap
                  latitude={imEvent.latitude}
                  longitude={imEvent.longitude}
                />
              </Box>
            )}
          {/* 行き方を調べるボタン */}
          {customerSetting.info.showMapLink &&
            imEvent.participationType !== 'online' &&
            imEvent.latitude !== null &&
            imEvent.longitude !== null && (
              <Box
                sx={{
                  my: RESPONSIVE_MARGIN,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <GeoMapLinkButton
                  latitude={imEvent.latitude}
                  longitude={imEvent.longitude}
                />
              </Box>
            )}
          {/* 参照サイト */}
          {imEvent.url && (
            <Box sx={{ my: RESPONSIVE_MARGIN }}>
              <Reference url={imEvent.url} siteName={imEvent.siteName} />
            </Box>
          )}
          {/* 掲載ミスなどに関する注意書き */}
          {customerSetting.info.annotations.length > 0 && (
            <Box sx={{ my: RESPONSIVE_MARGIN }}>
              {customerSetting.info.annotations.map((annotation, i) => (
                <MarkdownText
                  key={`annotation-${i}`}
                  markdown={annotation}
                  size="small"
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
